/**
 * useMentionSearch — searches entities by name for @-mention autocomplete.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { EntitySearchResult } from "../types";

interface UseMentionSearchResult {
  readonly results: readonly EntitySearchResult[];
  readonly isLoading: boolean;
}

export function useMentionSearch(
  query: string,
  active: boolean,
  schemaFilter?: string,
): UseMentionSearchResult {
  const runtime = useAppRuntime();
  const [results, setResults] = useState<readonly EntitySearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef(0); // generation counter to discard stale results

  const doSearch = useCallback(
    async (q: string, gen: number) => {
      try {
        setIsLoading(true);
        const params: Record<string, unknown> = { limit: 8 };
        if (q) params.query = q;
        if (schemaFilter) params.schema_ids = [schemaFilter];

        const resp = await runtime.transport.rpc<{
          items: readonly EntitySearchResult[];
        }>("graph.entity.search", params);

        // Only apply results if this is still the latest request
        if (abortRef.current === gen) {
          setResults(resp.items);
        }
      } catch {
        if (abortRef.current === gen) {
          setResults([]);
        }
      } finally {
        if (abortRef.current === gen) {
          setIsLoading(false);
        }
      }
    },
    [runtime.transport, schemaFilter],
  );

  useEffect(() => {
    if (!active) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    clearTimeout(timerRef.current);
    const gen = ++abortRef.current;

    timerRef.current = setTimeout(() => {
      void doSearch(query, gen);
    }, 50);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { clearTimeout(timerRef.current); };
  }, [query, active, doSearch]);

  return { results, isLoading };
}
